<template>
  <CurrentEventDataProvider>
    <ParticipantDashboardLayout>
      <div class="p-6 md:p-8 space-y-6 w-full">
        <!-- Header -->
        <header class="flex flex-col gap-6">
          <div class="flex items-start gap-3 min-w-0">
            <button
              type="button"
              class="shrink-0 inline-flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 text-sm sm:text-base rounded-xl border border-slate-200 text-slate-600 hover:text-teal-600 hover:border-teal-300 transition-colors"
              aria-label="Back to dashboard"
              title="Back to dashboard"
              @click="$router.push({ name: 'dashboard' }).catch(() => {})"
            >
              <i class="fal fa-arrow-left"></i>
            </button>
            <div class="min-w-0">
              <h1
                class="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight"
              >
                {{ $t('Views.MyMeetings.title') }}
              </h1>
              <p class="text-sm text-slate-500 mt-1">
                {{ $t('Views.MyMeetings.subtitle') }}
              </p>
            </div>
          </div>
          <NetworkingQuickNav />
        </header>

        <!-- Data Provider -->
        <MyMeetingsDataProvider
          :tab="activeTab"
          v-slot="{
            meetings,
            loading,
            pendingCount,
            upcomingCount,
            refresh,
            cancelMeeting,
            acceptMeeting,
            declineMeeting
          }"
        >
          <div class="space-y-6">
            <!-- Quick Stats Grid -->
            <section class="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <!-- Upcoming Meetings -->
              <div
                class="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm flex items-center justify-between"
              >
                <div class="space-y-1">
                  <span
                    class="text-xxs font-extrabold uppercase tracking-widest text-slate-400"
                  >
                    {{ $t('Views.MyMeetings.statUpcoming') }}
                  </span>
                  <div class="text-2xl font-black text-slate-900">
                    {{ upcomingCount }}
                  </div>
                </div>
                <div
                  class="w-10 h-10 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center shrink-0"
                >
                  <i class="fal fa-calendar-check text-lg"></i>
                </div>
              </div>

              <!-- Pending Invitations -->
              <div
                class="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm flex items-center justify-between"
              >
                <div class="space-y-1">
                  <span
                    class="text-xxs font-extrabold uppercase tracking-widest text-slate-400"
                  >
                    {{ $t('Views.MyMeetings.statPending') }}
                  </span>
                  <div class="text-2xl font-black text-amber-600">
                    {{ pendingCount }}
                  </div>
                </div>
                <div
                  class="w-10 h-10 rounded-xl bg-amber-50 text-amber-500 flex items-center justify-center shrink-0"
                >
                  <i class="fal fa-hourglass-half text-lg animate-pulse"></i>
                </div>
              </div>
            </section>

            <!-- Search (filters the active tab) -->
            <div>
              <SearchPill
                v-model="searchQuery"
                :placeholder="$t('Views.MyMeetings.searchPlaceholder')"
                class="w-full"
              />
            </div>

            <!-- Navigation Tabs + Request a meeting action on the same row -->
            <div
              class="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between sm:gap-4 sm:border-b sm:border-slate-200"
            >
              <nav
                class="flex gap-4 sm:gap-8 border-b border-slate-200 sm:border-b-0"
              >
                <button
                  v-for="tab in tabs"
                  :key="tab"
                  type="button"
                  :class="[
                    'pb-4 pt-3 sm:pt-0 px-1 text-sm font-bold relative transition-all duration-200 outline-none flex items-center gap-2 whitespace-nowrap min-h-[44px] sm:min-h-0',
                    activeTab === tab
                      ? 'text-teal-600 font-extrabold border-b-2 border-teal-600 -mb-px'
                      : 'text-slate-400 hover:text-slate-700'
                  ]"
                  @click="activeTab = tab"
                >
                  <span>{{ $t('Views.MyMeetings.tab' + tabKey(tab)) }}</span>
                  <span
                    v-if="tab === 'pending' && pendingCount > 0"
                    class="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-black"
                  >
                    {{ pendingCount }}
                  </span>
                  <span
                    v-if="tab === 'upcoming' && upcomingCount > 0"
                    class="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-teal-50 text-teal-600 border border-teal-200 text-[10px] font-black"
                  >
                    {{ upcomingCount }}
                  </span>
                </button>
              </nav>

              <button
                type="button"
                class="shrink-0 w-full sm:w-auto sm:mb-2 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl btn-brand text-xs font-extrabold uppercase tracking-wider transition-all shadow-sm min-h-[44px]"
                @click="openSponsorPicker"
              >
                <i class="fal fa-plus"></i>
                <span>{{ $t('Views.MyMeetings.requestMeeting') }}</span>
              </button>
            </div>

            <!-- Loading & Empty States -->
            <div
              v-if="loading && meetings.length === 0"
              class="py-16 bg-white rounded-2xl border border-slate-200 text-center text-slate-500 shadow-sm"
            >
              <i class="fal fa-spinner-third animate-spin mr-2"></i>
              {{ $t('Views.MyMeetings.loading') }}
            </div>

            <div
              v-else-if="meetings.length === 0"
              class="py-16 bg-white rounded-2xl border border-slate-200 text-center text-slate-500 px-4 shadow-sm"
            >
              <i
                class="fal fa-calendar-times text-2xl text-slate-300 block mb-2"
              ></i>
              <p class="text-sm font-semibold text-slate-700">
                {{ emptyMessage }}
              </p>
            </div>

            <div
              v-else-if="filteredMeetings(meetings).length === 0"
              class="py-16 bg-white rounded-2xl border border-slate-200 text-center text-slate-500 px-4 shadow-sm"
            >
              <i class="fal fa-search text-2xl text-slate-300 block mb-2"></i>
              <p class="text-sm font-semibold text-slate-700">
                {{ $t('Views.MyMeetings.noResults', { query: searchQuery }) }}
              </p>
            </div>

            <!-- Meetings List.
                 `items-start` on every flex level keeps the calendar block aligned with the
                 sponsor name (top-left of the card) regardless of how tall the meeting body
                 grows. Without this, `items-center` would float the calendar to the vertical
                 center, which shifts unpredictably between cards that have notes/reasons and
                 ones that don't — that's the misalignment users see when scrolling the list. -->
            <div
              v-else
              class="flex flex-col xl:flex-row gap-4 items-start animate-fade-in"
            >
              <div
                v-for="(col, ci) in meetingColumns(filteredMeetings(meetings))"
                :key="'col-' + ci"
                class="flex-1 min-w-0 w-full flex flex-col gap-4"
              >
                <div
                  v-for="m in col"
                  :key="m.id"
                  class="flex items-stretch bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden transition-all duration-150 hover:border-teal-500 hover:shadow-md"
                >
                  <!-- Left: time + status (matches My Programs card) -->
                  <div
                    class="flex flex-col items-start justify-start px-4 py-6 min-w-[110px] border-r border-slate-200 gap-1 shrink-0"
                  >
                    <i class="far fa-clock text-teal-600 text-[13px] mb-1"></i>
                    <span
                      class="text-[18px] font-bold text-slate-900 leading-none"
                    >
                      {{ meetingTime(m.requestedStartAt) }}
                    </span>
                    <span class="text-sm text-slate-500 leading-tight mb-2">
                      {{ meetingTime(m.requestedEndAt) }}
                    </span>
                    <span
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider border leading-none"
                      :class="badgeClasses(m.status)"
                    >
                      {{ statusLabel(m.status) }}
                    </span>
                  </div>

                  <!-- Right: body -->
                  <div class="flex-1 min-w-0 px-5 py-6">
                    <div class="flex items-start justify-between gap-3">
                      <h3
                        class="text-base font-bold text-slate-900 mb-1 leading-tight min-w-0"
                      >
                        {{ m.sponsorName }}
                      </h3>
                      <!-- Cancel — cross at the top-right of the card. -->
                      <button
                        v-if="canCancel(m)"
                        type="button"
                        class="shrink-0 -mr-1 w-8 h-8 inline-flex items-center justify-center rounded-lg bg-slate-100 text-slate-500 hover:bg-red-50 hover:text-red-500 transition-colors"
                        :aria-label="$t('Views.MyMeetings.cancel')"
                        :title="$t('Views.MyMeetings.cancel')"
                        @click="askCancel(m, cancelMeeting, refresh)"
                      >
                        <i
                          class="fas fa-times text-[13px]"
                          aria-hidden="true"
                        ></i>
                      </button>
                    </div>
                    <div
                      class="flex flex-wrap gap-x-3.5 gap-y-1 text-sm text-slate-500"
                    >
                      <span class="inline-flex items-center gap-1.5">
                        <i class="far fa-calendar text-[12px]"></i>
                        {{ meetingDate(m.requestedStartAt) }}
                      </span>
                      <span
                        v-if="m.location"
                        class="inline-flex items-center gap-1.5"
                      >
                        <i class="fas fa-map-marker-alt text-[12px]"></i>
                        {{ m.location }}
                      </span>
                    </div>

                    <!-- User Note -->
                    <div
                      v-if="m.message"
                      class="mt-3 max-w-xl rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 leading-relaxed"
                    >
                      <span
                        class="block text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-1"
                        >{{ $t('Views.MyMeetings.requestNoteLabel') }}</span
                      >
                      <span class="text-xs font-medium text-slate-600">{{
                        m.message
                      }}</span>
                    </div>

                    <!-- Decline Reason -->
                    <div
                      v-if="m.declineReason"
                      class="mt-3 p-3 bg-red-50/50 border border-red-100 rounded-xl text-xxs text-red-600 font-medium max-w-xl leading-relaxed flex gap-2"
                    >
                      <i class="fal fa-info-circle shrink-0 mt-0.5"></i>
                      <div>
                        <span
                          class="text-[9px] font-bold text-red-500 uppercase tracking-wider block mb-0.5"
                          >{{ $t('Views.MyMeetings.declineReasonLabel') }}</span
                        >
                        {{ m.declineReason }}
                      </div>
                    </div>

                    <!-- Cancel Reason -->
                    <div
                      v-if="m.cancelReason"
                      class="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-xl text-xxs text-slate-500 font-medium max-w-xl leading-relaxed flex gap-2"
                    >
                      <i class="fal fa-info-circle shrink-0 mt-0.5"></i>
                      <div>
                        <span
                          class="text-[9px] font-bold text-slate-400 uppercase tracking-wider block mb-0.5"
                          >{{ $t('Views.MyMeetings.cancelReasonLabel') }}</span
                        >
                        {{ m.cancelReason }}
                      </div>
                    </div>

                    <!-- Actions (bottom-right of the body) -->
                    <div
                      v-if="canRespond(m) || isAwaitingSponsor(m)"
                      class="mt-4 flex flex-wrap items-center justify-end gap-2"
                    >
                      <!-- Sponsor-initiated request awaiting this participant. -->
                      <template v-if="canRespond(m)">
                        <button
                          type="button"
                          class="px-4 py-2 text-xs font-bold btn-brand border rounded-lg transition-all"
                          @click="onAccept(m, acceptMeeting, refresh)"
                        >
                          {{ $t('Views.MyMeetings.accept') }}
                        </button>
                        <button
                          type="button"
                          class="px-4 py-2 text-xs font-bold text-red-500 hover:bg-red-50/50 border border-red-200 rounded-lg transition-all"
                          @click="askDecline(m, declineMeeting, refresh)"
                        >
                          {{ $t('Views.MyMeetings.decline') }}
                        </button>
                      </template>

                      <!-- Participant-initiated request still pending. -->
                      <span
                        v-else-if="isAwaitingSponsor(m)"
                        class="text-xs font-semibold text-slate-400 inline-flex items-center"
                      >
                        {{ $t('Views.MyMeetings.awaitingSponsor') }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </MyMeetingsDataProvider>
      </div>
    </ParticipantDashboardLayout>
  </CurrentEventDataProvider>
</template>

<script>
import ParticipantDashboardLayout from '@/components/shared/ParticipantDashboardLayout.vue';
import NetworkingQuickNav from '@/components/aiMatching/NetworkingQuickNav.vue';
import SearchPill from '@/components/shared/SearchPill.vue';
import SponsorPickerModal from '@/components/sponsor/SponsorPickerModal.vue';
import { formatEventDateTime } from '@/helpers/event-time';
import { englishOnlyI18nMixin } from '@/mixins';

const TABS = ['upcoming', 'pending', 'past'];

export default {
  name: 'MyMeetings',
  components: {
    ParticipantDashboardLayout,
    NetworkingQuickNav,
    SearchPill
  },
  mixins: [englishOnlyI18nMixin],
  data() {
    return {
      activeTab: 'upcoming',
      tabs: TABS,
      searchQuery: '',
      // Drives the meetings list column count (2 columns at xl+, 1 below).
      isWide: false
    };
  },
  mounted() {
    this.updateIsWide();
    window.addEventListener('resize', this.updateIsWide);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateIsWide);
  },
  computed: {
    emptyMessage() {
      const key = 'empty' + this.tabKey(this.activeTab);
      return this.$t('Views.MyMeetings.' + key);
    },
    eventTz() {
      return this.$store.getters['CurrentEvent/getTimezone'];
    },
    eventMode() {
      return this.$store.getters['CurrentEvent/getTimezoneMode'];
    }
  },
  methods: {
    updateIsWide() {
      this.isWide = typeof window !== 'undefined' && window.innerWidth >= 1280;
    },
    // Split the meetings into N balanced columns (round-robin keeps the
    // natural left-to-right, top-to-bottom reading order) so the masonry
    // is plain flex columns with even gaps — no fragile CSS multi-column.
    meetingColumns(list) {
      const cols = this.isWide ? 2 : 1;
      const out = Array.from({ length: cols }, () => []);
      (list || []).forEach((item, i) => out[i % cols].push(item));
      return out;
    },
    openSponsorPicker() {
      this.$modal.show(
        SponsorPickerModal,
        {},
        {
          adaptive: true,
          minWidth: 320,
          maxWidth: 600,
          width: '90%',
          height: 'auto'
        }
      );
    },
    tabKey(t) {
      return t.charAt(0).toUpperCase() + t.slice(1);
    },
    statusLabel(status) {
      // Map the raw backend enum to a translated, humanised label ("No Show", not "NoShow").
      const key = 'status' + status;
      const label = this.$t('Views.MyMeetings.' + key);
      // $t returns the key path back when missing — fall back to the raw status in that case.
      return label === 'Views.MyMeetings.' + key ? status : label;
    },
    filteredMeetings(list) {
      const q = this.searchQuery.trim().toLowerCase();
      if (!q) return list;
      return list.filter((m) => {
        const hay = `${m.sponsorName || ''} ${m.message || ''} ${
          m.location || ''
        }`.toLowerCase();
        return hay.includes(q);
      });
    },
    getCalendarBlock(startIso) {
      const month = formatEventDateTime(
        startIso,
        this.eventTz,
        this.eventMode,
        {
          month: 'short'
        }
      );
      const day = formatEventDateTime(startIso, this.eventTz, this.eventMode, {
        day: 'numeric'
      });
      return {
        month: month ? month.toUpperCase() : 'EVENT',
        day: day || '?'
      };
    },
    badgeClasses(status) {
      return (
        {
          Pending: 'bg-amber-50 text-amber-600 border-amber-100',
          Confirmed: 'bg-emerald-50 text-emerald-600 border-emerald-100',
          Declined: 'bg-red-50 text-red-600 border-red-100',
          Cancelled: 'bg-slate-100 text-slate-400 border-slate-200',
          Completed: 'bg-blue-50 text-blue-600 border-blue-100',
          NoShow: 'bg-rose-50 text-rose-600 border-rose-100'
        }[status] || 'bg-slate-100 text-slate-500 border-slate-200'
      );
    },
    // Individual time / date parts for the My-Programs-style card layout.
    meetingTime(iso) {
      return (
        formatEventDateTime(iso, this.eventTz, this.eventMode, {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        }) || ''
      );
    },
    meetingDate(iso) {
      return (
        formatEventDateTime(iso, this.eventTz, this.eventMode, {
          month: 'short',
          day: 'numeric'
        }) || ''
      );
    },
    formatRange(startIso, endIso) {
      const day = formatEventDateTime(startIso, this.eventTz, this.eventMode, {
        month: 'short',
        day: 'numeric'
      });
      const start = formatEventDateTime(
        startIso,
        this.eventTz,
        this.eventMode,
        {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        }
      );
      const end = formatEventDateTime(endIso, this.eventTz, this.eventMode, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      if (!day || !start || !end) return 'Scheduling...';
      return `${day}, ${start} – ${end}`;
    },
    canCancel(meeting) {
      // Only future meetings are cancellable — a meeting that has already ended can't be cancelled,
      // even if it's still Confirmed/Pending (it just hasn't been closed out yet).
      const isOpen =
        meeting.status === 'Pending' || meeting.status === 'Confirmed';
      return isOpen && new Date(meeting.requestedEndAt).getTime() > Date.now();
    },
    async askCancel(meeting, cancelMeeting, refresh) {
      const reason =
        window.prompt(this.$t('Views.MyMeetings.cancelPrompt')) || '';
      if (reason === null) return;
      try {
        await cancelMeeting({
          meetingId: meeting.id,
          reason,
          refreshTab: this.activeTab
        });
      } finally {
        if (refresh) await refresh();
      }
    },
    canRespond(meeting) {
      // Accept/Decline only apply to a sponsor-initiated request that is still pending and
      // hasn't already started — mirrors the backend guard (Initiator==Sponsor && Pending).
      return (
        meeting.initiator === 'Sponsor' &&
        meeting.status === 'Pending' &&
        new Date(meeting.requestedEndAt).getTime() > Date.now()
      );
    },
    isAwaitingSponsor(meeting) {
      // A participant-initiated request that's still pending is the sponsor's to act on.
      return (
        meeting.initiator === 'Participant' && meeting.status === 'Pending'
      );
    },
    async onAccept(meeting, acceptMeeting, refresh) {
      try {
        await acceptMeeting({
          meetingId: meeting.id,
          refreshTab: this.activeTab
        });
      } finally {
        if (refresh) await refresh();
      }
    },
    async askDecline(meeting, declineMeeting, refresh) {
      const reason =
        window.prompt(this.$t('Views.MyMeetings.declinePrompt')) || '';
      if (reason === null) return;
      try {
        await declineMeeting({
          meetingId: meeting.id,
          reason,
          refreshTab: this.activeTab
        });
      } finally {
        if (refresh) await refresh();
      }
    }
  }
};
</script>

<style scoped>
.w-16 {
  width: 4rem !important;
  height: 4rem !important;
  min-width: 4rem !important;
  min-height: 4rem !important;
}
.h-16 {
  height: 4rem !important;
}
.text-xxs {
  font-size: 0.65rem;
}
.border-slate-150 {
  border-color: #e2e8f0;
}
.animate-fade-in {
  animation: fadeIn 0.3s ease-out forwards;
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Strictly prevent FontAwesome icons/SVGs from expanding enormously */
i.fal,
i.fas,
i.far,
i.fab,
svg {
  width: 1.125rem !important;
  height: 1.125rem !important;
  min-width: 1.125rem !important;
  min-height: 1.125rem !important;
  max-width: 1.125rem !important;
  max-height: 1.125rem !important;
  font-size: 1.125rem !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex-shrink: 0 !important;
}

/* Spinner Icon */
.fa-spinner-third {
  width: 1.25rem !important;
  height: 1.25rem !important;
  min-width: 1.25rem !important;
  min-height: 1.25rem !important;
  font-size: 1.25rem !important;
}

/* Large stats block icons and empty state icons sizing */
.w-10 i,
.w-10 svg,
.text-2xl i,
.text-2xl svg {
  width: 1.5rem !important;
  height: 1.5rem !important;
  min-width: 1.5rem !important;
  min-height: 1.5rem !important;
  max-width: 1.5rem !important;
  max-height: 1.5rem !important;
  font-size: 1.5rem !important;
}
</style>
